// Generic CRUD list component for Investors, Partners, News
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, MoreHorizontal, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Investors List
export const InvestorsList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [investorsRes, fundsRes] = await Promise.all([
          axios.get(`${API}/admin/investors?include_inactive=true`, { withCredentials: true }),
          axios.get(`${API}/admin/funds`, { withCredentials: true })
        ]);
        setItems(investorsRes.data);
        setFunds(fundsRes.data);
      } catch (error) { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleDelete = async () => {
    try {
      console.log('Attempting to delete investor:', deleteId);
      const response = await axios.delete(`${API}/admin/investors/${deleteId}`, { withCredentials: true });
      console.log('Delete response:', response.data);
      toast.success('Deleted');
      setItems(items.filter(i => i.investor_id !== deleteId));
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setDeleteId(null);
    }
  };

  const getFundName = (id) => funds.find(f => f.fund_id === id)?.name || id;
  const filtered = items.filter(i => i.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Investors</h1><p className="text-gray-500">Manage LPs and investors</p></div>
        <Button onClick={() => navigate('/admin/investors/new')} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> Add Investor</Button>
      </div>
      <div className="mb-6"><div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader><TableRow><TableHead>Investor</TableHead><TableHead>Fund</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow> :
            filtered.map(item => (
              <TableRow key={item.investor_id}>
                <TableCell><div className="flex items-center gap-3">{item.logo_url ? <img src={item.logo_url.startsWith('/') ? `${BACKEND_URL}${item.logo_url}` : item.logo_url} alt={item.name} className="w-10 h-10 rounded-lg object-contain bg-gray-50" /> : <div className="w-10 h-10 rounded-lg bg-gray-100" />}<div><p className="font-medium">{item.name}</p>{item.description && <p className="text-sm text-gray-500 truncate max-w-xs">{item.description}</p>}</div></div></TableCell>
                <TableCell>{getFundName(item.fund_id)}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{item.investor_type?.replace('_', ' ')}</Badge></TableCell>
                <TableCell><Badge variant={item.is_active ? 'default' : 'secondary'}>{item.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/investors/${item.investor_id}`)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(item.investor_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

// Partners List
export const PartnersList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/partners?include_inactive=true`, { withCredentials: true })
      .then(res => setItems(res.data))
      .catch(() => toast.error('Failed'))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async () => {
    try {
      console.log('Attempting to delete partner:', deleteId);
      const response = await axios.delete(`${API}/admin/partners/${deleteId}`, { withCredentials: true });
      console.log('Delete response:', response.data);
      toast.success('Deleted');
      setItems(items.filter(i => i.partner_id !== deleteId));
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = items.filter(i => i.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Ecosystem Partners</h1><p className="text-gray-500">Manage partners</p></div>
        <Button onClick={() => navigate('/admin/partners/new')} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> Add Partner</Button>
      </div>
      <div className="mb-6"><div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader><TableRow><TableHead>Partner</TableHead><TableHead>Type</TableHead><TableHead>Caption</TableHead><TableHead>Status</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow> :
            filtered.map(item => (
              <TableRow key={item.partner_id}>
                <TableCell><div className="flex items-center gap-3">{item.logo_url ? <img src={item.logo_url.startsWith('/') ? `${BACKEND_URL}${item.logo_url}` : item.logo_url} alt={item.name} className="w-10 h-10 rounded-lg object-contain bg-gray-50" /> : <div className="w-10 h-10 rounded-lg bg-gray-100" />}<p className="font-medium">{item.name}</p></div></TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{item.partner_type}</Badge></TableCell>
                <TableCell className="max-w-xs truncate">{item.caption}</TableCell>
                <TableCell><Badge variant={item.is_active ? 'default' : 'secondary'}>{item.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/partners/${item.partner_id}`)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(item.partner_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

// News List
export const NewsList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/news?include_inactive=true`, { withCredentials: true })
      .then(res => setItems(res.data))
      .catch(() => toast.error('Failed'))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async () => {
    try {
      console.log('Attempting to delete news:', deleteId);
      const response = await axios.delete(`${API}/admin/news/${deleteId}`, { withCredentials: true });
      console.log('Delete response:', response.data);
      toast.success('Deleted');
      setItems(items.filter(i => i.article_id !== deleteId));
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = items.filter(i => i.title?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">News Articles</h1><p className="text-gray-500">Manage news</p></div>
        <Button onClick={() => navigate('/admin/news/new')} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> Add News</Button>
      </div>
      <div className="mb-6"><div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
      <div className="bg-white rounded-lg border overflow-x-auto">
        <Table className="table-fixed w-full min-w-[600px]">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0 w-[40%]">Title</TableHead>
              <TableHead className="whitespace-nowrap">Source</TableHead>
              <TableHead className="whitespace-nowrap">Date</TableHead>
              <TableHead className="whitespace-nowrap">Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow> :
            filtered.map(item => (
              <TableRow key={item.article_id}>
                <TableCell className="min-w-0 max-w-0 w-[40%]">
                  <p className="font-medium truncate" title={item.title}>{item.title}</p>
                  <p className="text-sm text-gray-500 truncate" title={item.snippet}>{item.snippet}</p>
                </TableCell>
                <TableCell className="whitespace-nowrap"><a href={item.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600">{item.source_name} <ExternalLink className="w-3 h-3 flex-shrink-0" /></a></TableCell>
                <TableCell className="whitespace-nowrap">{new Date(item.published_date).toLocaleDateString()}</TableCell>
                <TableCell className="whitespace-nowrap"><Badge variant={item.is_active ? 'default' : 'secondary'}>{item.is_featured ? 'Featured' : item.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell className="w-12">
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/news/${item.article_id}`)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(item.article_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

// Regulatory Updates List
export const RegulatoryUpdatesList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/regulatory-updates?include_inactive=true`, { withCredentials: true })
      .then(res => setItems(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/regulatory-updates/${deleteId}`, { withCredentials: true });
      toast.success('Deleted');
      setItems(items.filter(i => i.update_id !== deleteId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = items.filter(i =>
    (i.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (i.summary || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Regulatory Updates</h1><p className="text-gray-500">Manage regulatory updates for Resources page</p></div>
        <Button onClick={() => navigate('/admin/regulatory-updates/new')} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> Add Update</Button>
      </div>
      <div className="mb-6"><div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Summary</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow> :
            filtered.map(item => (
              <TableRow key={item.update_id}>
                <TableCell><p className="font-medium max-w-xs truncate">{item.title}</p></TableCell>
                <TableCell><p className="text-sm text-gray-500 max-w-md truncate">{item.summary}</p></TableCell>
                <TableCell>{new Date(item.published_date).toLocaleDateString()}</TableCell>
                <TableCell><Badge variant={item.is_active ? 'default' : 'secondary'}>{item.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/regulatory-updates/${item.update_id}`)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(item.update_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

// Events List
export const EventsList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/events?include_inactive=true`, { withCredentials: true })
      .then(res => setItems(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/events/${deleteId}`, { withCredentials: true });
      toast.success('Deleted');
      setItems(items.filter(i => i.event_id !== deleteId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = items.filter(i => (i.title || '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Events</h1><p className="text-gray-500">Manage events</p></div>
        <Button onClick={() => navigate('/admin/events/new')} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> Add Event</Button>
      </div>
      <div className="mb-6"><div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={4} className="text-center py-8">Loading...</TableCell></TableRow> :
            filtered.map(item => (
              <TableRow key={item.event_id}>
                <TableCell><p className="font-medium max-w-md truncate">{item.title}</p></TableCell>
                <TableCell>{item.event_date ? new Date(item.event_date).toLocaleDateString() : '—'}</TableCell>
                <TableCell><Badge variant={item.is_active ? 'default' : 'secondary'}>{item.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/events/${item.event_id}`)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(item.event_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};
